import type { BadgeTone } from '../../components/ui';
import type { Enums, Tables } from '../../types/database.types';

export type AppNotification = Tables<'notifications'>;
export type TaskMute = Tables<'task_mutes'>;

export type NotificationEvent = Enums<'notification_event'>;

/**
 * An Inbox item joined to what it points at. Each joined field is null when
 * the row is gone or the reader has since lost access to it — the database
 * writes a notification only for people who could see the project at the
 * time, and revoking access later leaves the pointer dangling on purpose.
 * The Inbox renders those as unavailable rather than hiding history.
 */
export interface InboxItem {
  id: string;
  event_type: NotificationEvent;
  project_id: string | null;
  task_id: string | null;
  comment_id: string | null;
  details: Record<string, unknown>;
  is_read: boolean;
  created_at: string;
  /** Empty when the actor is the system (the due-date job) or unreadable. */
  actor_name: string;
  /** Null for system rows, for actors without a Google picture, or unreadable. */
  actor_avatar_url: string | null;
  task_title: string | null;
  project_name: string | null;
  /** Set on a mention in a deal comment. Exactly one parent, as on the comment. */
  deal_id: string | null;
  /** Set on a mention in a social post comment. */
  social_post_id: string | null;
  /**
   * Set on CG order events, which have no project. Exactly one of project_id
   * and cg_order_id is meaningful on any row — see notificationHref, which
   * routes on that and would otherwise build /projects/null.
   */
  cg_order_id: string | null;
  cg_order_no: string | null;
  /**
   * The order's pipeline, for the badge on a CG row. Null when the order is
   * gone or unreadable — the same "unavailable" shape as the order number
   * beside it, and the badge is simply not drawn.
   */
  cg_order_type: string | null;
  /**
   * npc r3: 'messages' on a notice about Messages activity (a room, DM, group
   * or thread reply, or a reply in the Alex Huddle chat), null otherwise. Set
   * by a BEFORE INSERT trigger on notifications; the browser only reads it.
   * isMessagesNotification is the one place that asks.
   */
  source: string | null;
}

/**
 * Where a notification row goes when it is clicked.
 *
 * Moved out of InboxPage so the routing has a test. It used to be one line
 * that always built `/projects/<project_id>`, which was correct while every
 * notification belonged to a project. CG order events do not — they carry a
 * cg_order_id and a NULL project — so that line would have produced the string
 * "/projects/null": a real route, a real navigation, and a page that renders
 * nothing with no error to explain it.
 *
 * The order of the checks is the rule. A row carries at most one of these, but
 * checking the CG id first means that if a future event ever carries both, it
 * lands on the more specific of the two rather than silently on the project.
 */
export function notificationHref(item: InboxItem): string {
  // A PRESENT, non-empty id — not merely "not null". A row that predates this
  // column, or one built by a caller that does not know about it, has the
  // field absent rather than null, and `!== null` is true for undefined. That
  // sent every ordinary project notification to "/cg-orders?order=undefined".
  if (typeof item.cg_order_id === 'string' && item.cg_order_id !== '') {
    // /cg-orders is a list, not a detail route. The query parameter is what
    // the page opens on arrival; without it the person lands on a table and
    // has to find the order the notification was already about.
    return `/cg-orders?order=${encodeURIComponent(item.cg_order_id)}`;
  }
  // Same shape as the cg_order branch above, and the same reason: /deals is a
  // list, so the query parameter is what opens the deal the notification was
  // already about. Without it somebody lands on a board and has to hunt.
  if (typeof item.deal_id === 'string' && item.deal_id !== '') {
    // The comment rides along, as it does on the project branch below. Ced,
    // 21 September: a mention opened the deal and then left him to search the
    // thread for what was said. The row has carried comment_id since the
    // deal-mention trigger shipped; this branch was the one place dropping it.
    // encodeURIComponent, not URLSearchParams: the latter writes a space as
    // "+", and every existing deal link has been written with "%20".
    const deal = `/deals?deal=${encodeURIComponent(item.deal_id)}`;
    return typeof item.comment_id === 'string' && item.comment_id !== ''
      ? `${deal}&comment=${encodeURIComponent(item.comment_id)}`
      : deal;
  }
  if (typeof item.social_post_id === 'string' && item.social_post_id !== '') {
    return `/social-studio?post=${encodeURIComponent(item.social_post_id)}`;
  }
  // Hiring Phase 2: these rows carry no project. An assignment names the
  // applicant (details.candidate_id) and opens them; a sync alert opens the
  // Hiring page itself, where the sync line says what is wrong.
  if (item.event_type === 'hiring_assigned' || item.event_type === 'hiring_alert') {
    const candidate = item.details['candidate_id'];
    return typeof candidate === 'string' && candidate !== ''
      ? `/hiring?candidate=${encodeURIComponent(candidate)}`
      : '/hiring';
  }
  if (typeof item.project_id !== 'string' || item.project_id === '') {
    // Nothing to point at. The Inbox renders these as unavailable rather than
    // navigating somewhere arbitrary.
    return '';
  }
  // Drop 3c (Dev4 A6): a thread reply opens the thread itself, not the room.
  // A room is a project that ProjectPage redirects to /messages and drops the
  // comment from, so /projects/<room>?comment= would leave the reader hunting.
  // details.parent_comment_id is written by the thread_reply trigger (Dev3).
  const threadParent = item.details['parent_comment_id'];
  if (
    item.event_type === 'thread_reply' &&
    typeof threadParent === 'string' &&
    threadParent !== ''
  ) {
    return `/messages?room=${encodeURIComponent(item.project_id)}&thread=${encodeURIComponent(threadParent)}`;
  }
  // npc r3: any other Messages notice without a task is about a room message
  // (an @ in a room, DM, group or thread reply, or being added to a room). It
  // opens Messages on that room and message; MessagesPage opens the thread
  // when the message is a reply. Before this it went to /projects/<room>,
  // which ProjectPage redirects to the room and drops the comment from.
  // A Messages notice WITH a task is the Alex Huddle chat (a task comment in
  // the owner's personal project) and keeps the task route below.
  if (isMessagesNotification(item) && (typeof item.task_id !== 'string' || item.task_id === '')) {
    const room = `/messages?room=${encodeURIComponent(item.project_id)}`;
    return typeof item.comment_id === 'string' && item.comment_id !== ''
      ? `${room}&comment=${encodeURIComponent(item.comment_id)}`
      : room;
  }
  const params = new URLSearchParams();
  if (item.task_id !== null) {
    params.set('task', item.task_id);
  }
  if (item.comment_id !== null) {
    params.set('comment', item.comment_id);
  }
  const query = params.toString();
  return `/projects/${item.project_id}${query === '' ? '' : `?${query}`}`;
}

export const NOTIFICATION_EVENT_LABELS: Record<NotificationEvent, string> = {
  // Deliberately says nothing about which way it went. The Inbox list is not
  // the place to announce that somebody's leave was refused; the outcome is on
  // the Leaves page, where the approver's note is guarded.
  leave_decided: 'decided your leave',
  task_assigned: 'assigned you a task',
  task_completed: 'completed your task',
  collaborator_added: 'added you to a task',
  mentioned: 'mentioned you',
  huddle_comment: 'commented on',
  due_date_changed: 'changed a due date',
  moved_to_for_review: 'moved a task to For Review',
  task_due_tomorrow: 'Task due tomorrow',
  task_overdue: 'Task overdue',
  revision_requested: 'requested a revision',
  file_submitted_for_review: 'submitted a file for review',
  sample_photos_uploaded: 'uploaded sample photos',
  supplier_review_approved: 'approved a supplier review',
  added_to_project: 'added you to a project',
  // The client, not a colleague — these rows carry no actor, so the label has
  // to name who acted or it reads as though nobody did.
  client_signed_off: 'Client signed off the order',
  // The actor IS resolvable here (the factory's own account), but naming a
  // supplier where the Inbox expects a colleague reads as though a teammate
  // pushed back on our own PO. The label names the side of the table instead.
  supplier_requested_revision: 'Supplier asked for a change to the PO',
  // No actor at all — nobody did this, which is the entire point of the
  // alert. A label naming a colleague would read as an accusation for an
  // order that simply stopped moving.
  cg_order_stalled: 'Order has stopped moving',
  // Names the change rather than the document. "PO revised" alone sends
  // somebody to open the order to find out what moved; the details carry the
  // field, and the Inbox sentence should too.
  po_revised: 'PO revised — the supplier must re-acknowledge',
  // The approval half of "the client approves or denies" (20261116000100).
  // Same no-actor reasoning as client_signed_off: the client is not a Hub
  // account, so the label names who acted.
  client_approved_design: 'Client approved a component',
  // The generic movement event. The sentence is completed from the details
  // (from → to) by the Inbox row; this is the fallback phrasing.
  cg_status_changed: 'Order moved to a new stage',
  // The three below are written to SUPPLIER inboxes and rendered in the
  // portal's own feed, not here — but the Records in this file are keyed by
  // the enum, so every value needs an entry, and an honest one beats a
  // placeholder if a row ever lands in a TAP inbox by a future bug.
  po_sent: 'PO sent to the supplier',
  samples_decided: 'Sample verdict delivered to the supplier',
  revision_answered: 'PO question answered for the supplier',
  // Written by the morning research job with no human actor. Like the two
  // due-date events above, this sentence narrates itself in the Inbox.
  research_returned: 'Prospect research is ready',
  // Drop 3c. The row names the room from details.room_name (InboxPage).
  thread_reply: 'replied in a thread',
  // Scoreboard v3.1. Written by the database when an Analyze request waited ten
  // minutes or failed (20270239000100). No human actor; the sentence narrates itself.
  analyze_stalled: 'Analyze needs a look: the report has not been written',
  // Hiring Phase 2 (20270240000100). Bell only, never Slack: names stay in the Hub.
  hiring_assigned: 'picked you to interview an applicant',
  // Written by the hiring watchdog, no human actor. The sentence narrates itself.
  hiring_alert: 'Hiring sync needs a look',
};

/**
 * The micro badge each row carries (redesign spec §Inbox). The sentence
 * already says what happened in words; the badge is there so a full list
 * can be triaged by shape alone, without reading any of it.
 */
/**
 * What a row is ABOUT, when the thing it points at cannot say so.
 *
 * Kyu, twice: "All TAP members still can't see which task or project they
 * were mentioned from." The rows he was looking at were not mentions. Until
 * 20261121000100 the supplier submission ping wrote `mentioned` with no
 * project, no task and no order — so the Inbox said "X mentioned you" and
 * stopped, because that is all a row with no target can be described by.
 *
 * Those rows are somebody's read history and are deliberately NOT being
 * rewritten. This reads their details instead and says the true thing about
 * them. New rows carry the right event and the order, and never reach here.
 *
 * Returns null when the row has nothing extra to say, which is the ordinary
 * case for every event that points at a task or a project.
 */
export function inboxSubject(item: InboxItem): string | null {
  const submission = item.details['supplier_submission_id'];
  if (typeof submission !== 'string' || submission === '') {
    return null;
  }
  const title = item.details['title'];
  return typeof title === 'string' && title.trim() !== '' ? title : 'a sample submission';
}

/**
 * The project a mention names, read out of the row rather than joined.
 *
 * A mention now reaches somebody who is not on the project — Kyu's rule:
 * "if someone is mentioned, they should be notified. Full stop." But the
 * Inbox reads `project_name` through a join that RLS filters, so for that
 * reader it comes back null and the row would have no name and nothing to
 * click. That is the bare mention row 20261121000100 was written to kill.
 *
 * So `comment_mentions_notify`, which is security definer and can see the
 * project, stamps the name into `details` at insert. This reads it back.
 */
export function mentionProjectName(item: InboxItem): string | null {
  const name = item.details['mention_project_name'];
  return typeof name === 'string' && name.trim() !== '' ? name : null;
}

/**
 * Whether this mention points at a project the reader cannot open.
 *
 * Distinct from `isUnavailable`, which means something was lost — a deleted
 * project, or access taken away. Nothing was lost here: they were never on it.
 * Saying "no longer available to you" would describe a change that never
 * happened and make a working notification read as broken.
 */
export function isMentionWithoutAccess(item: InboxItem): boolean {
  return item.event_type === 'mentioned' && item.details['mention_no_access'] === true;
}

/**
 * Whether this row is the supplier submission ping wearing the wrong event.
 *
 * Only the legacy rows can be: from 20261121000100 the ping is written as
 * `sample_photos_uploaded`, which the labels already describe correctly.
 */
export function isLegacySubmissionPing(item: InboxItem): boolean {
  return item.event_type === 'mentioned' && inboxSubject(item) !== null;
}

export const NOTIFICATION_EVENT_BADGES: Record<
  NotificationEvent,
  { label: string; tone: BadgeTone }
> = {
  leave_decided: { label: 'Leave', tone: 'neutral' },
  task_assigned: { label: 'Assigned', tone: 'brand' },
  task_completed: { label: 'Completed', tone: 'success' },
  collaborator_added: { label: 'Assigned', tone: 'brand' },
  added_to_project: { label: 'Project', tone: 'brand' },
  client_signed_off: { label: 'Signed off', tone: 'success' },
  mentioned: { label: 'Mention', tone: 'review' },
  huddle_comment: { label: 'Huddle', tone: 'brand' },
  due_date_changed: { label: 'Due date', tone: 'neutral' },
  task_due_tomorrow: { label: 'Due soon', tone: 'warning' },
  task_overdue: { label: 'Overdue', tone: 'danger' },
  moved_to_for_review: { label: 'Review', tone: 'review' },
  file_submitted_for_review: { label: 'Review', tone: 'review' },
  revision_requested: { label: 'Revision', tone: 'revision' },
  // Blocking, not informational: until somebody answers, the PO cannot be
  // built and the sample clock has not started. It gets the tone that says so.
  supplier_requested_revision: { label: 'Blocked', tone: 'danger' },
  // Warning, not danger. A stalled order needs looking at; it is not yet
  // known to be wrong, and spending the loudest tone on "check this" leaves
  // nothing louder for the things that are actually broken.
  cg_order_stalled: { label: 'Stalled', tone: 'warning' },
  // Warning, not danger: nothing is broken. But a revision voids the factory's
  // acceptance, so it is never merely informational either.
  po_revised: { label: 'PO revised', tone: 'warning' },
  sample_photos_uploaded: { label: 'Samples', tone: 'neutral' },
  supplier_review_approved: { label: 'Approved', tone: 'success' },
  client_approved_design: { label: 'Approved', tone: 'success' },
  // Neutral: routine movement is the healthy case, and a coloured badge on
  // every transition would drown the rows that need one.
  cg_status_changed: { label: 'Moved', tone: 'neutral' },
  po_sent: { label: 'PO sent', tone: 'brand' },
  samples_decided: { label: 'Verdict', tone: 'review' },
  revision_answered: { label: 'Answered', tone: 'neutral' },
  research_returned: { label: 'Research', tone: 'success' },
  thread_reply: { label: 'Thread', tone: 'brand' },
  analyze_stalled: { label: 'Analyze', tone: 'warning' },
  hiring_assigned: { label: 'Hiring', tone: 'brand' },
  hiring_alert: { label: 'Hiring', tone: 'warning' },
};

/**
 * The type filters the Inbox offers (handoff 23). Phase 4's supplier events
 * fold into these buckets when they arrive rather than growing the menu now.
 */
export const NOTIFICATION_FILTERS = [
  { key: 'all', label: 'All' },
  { key: 'unread', label: 'Unread' },
  { key: 'assigned', label: 'Assigned to me' },
  { key: 'mentions', label: 'Mentions' },
  { key: 'due', label: 'Due dates' },
] as const;

export type NotificationFilterKey = (typeof NOTIFICATION_FILTERS)[number]['key'];

/* ── The CG Orders carve-out ───────────────────────────────────────────────
 *
 * Kyu: CG Orders gets "its own tab, filter or section", both Custom and
 * In-Stock in ONE bucket, and the general stream left alone.
 *
 * ── Why a tab and not a sixth chip ────────────────────────────────────────
 *
 * The chips above are a TYPE axis: what kind of event this is. CG-versus-
 * everything-else is a SOURCE axis. A sixth chip would put two axes in one
 * row, and — worse — would leave "All" still meaning "including CG", so the
 * general stream never actually gets quieter. That is the whole point of the
 * exercise, so the split has to be above the chips rather than inside them.
 *
 * ── Why the line is the order id and not the event name ───────────────────
 *
 * Nine events belong to CG Orders and they all carry a cg_order_id, written
 * directly by their triggers. Five more LOOK like CG work —
 * file_submitted_for_review, sample_photos_uploaded, supplier_review_approved,
 * moved_to_for_review, revision_requested — and carry a project and a task
 * instead, because they are Supplier Reviews inside projects. Splitting by
 * name would have put those in the wrong bucket, and would drift again the
 * next time somebody adds an event.
 *
 * It also settles "both Custom and In-Stock in one bucket, not two" for free:
 * both kinds of order have an id, so neither needs mentioning here.
 */

/* ── The Messages carve-out (npc r3) ───────────────────────────────────────
 *
 * Kyu, 28 Sept: message @ notices go to the Inbox ONLY under their own tab,
 * "Messages", like CG Orders, so chats do not get buried; never in General.
 * Same SOURCE axis as CG, so the same shape: a tab, not a chip.
 *
 * The line is the row's own marker, notifications.source = 'messages', which
 * the database sets at insert for every room notice (channel, DM, group,
 * thread reply) and for replies in the Alex Huddle chat. The page never
 * guesses from the event name: a 'mentioned' row is Messages in a room and
 * General on a project, and only the database knows which the project is.
 */

export const INBOX_TABS = [
  { key: 'general', label: 'General' },
  { key: 'cg', label: 'CG Orders' },
  { key: 'messages', label: 'Messages' },
] as const;

export type InboxTabKey = (typeof INBOX_TABS)[number]['key'];

/** A notification belongs to CG Orders when it points at an order. */
export function isCgNotification(item: InboxItem): boolean {
  return typeof item.cg_order_id === 'string' && item.cg_order_id !== '';
}

/** A notification belongs to Messages when the database marked it so. */
export function isMessagesNotification(item: InboxItem): boolean {
  return item.source === 'messages';
}

/**
 * The one tab a row lives in. CG is asked first: a CG row carries an order
 * and no project, so it can never be a room notice, but if a future row ever
 * carried both it lands on the more specific tab rather than on two.
 */
export function inboxTabOf(item: InboxItem): InboxTabKey {
  if (isCgNotification(item)) {
    return 'cg';
  }
  if (isMessagesNotification(item)) {
    return 'messages';
  }
  return 'general';
}

export function filterInboxByTab(items: InboxItem[], tab: InboxTabKey): InboxItem[] {
  return items.filter((item) => inboxTabOf(item) === tab);
}

/**
 * Which chips a tab can honestly offer.
 *
 * Assigned to me, Mentions and Due dates all match on event types no CG
 * notification can have — measured against FILTER_EVENTS below, not guessed.
 * On the CG tab they would sit there returning zero for ever, which reads as
 * broken rather than as empty. The Messages tab is asked the same question
 * against MESSAGES_EVENTS; nothing here names a chip per tab.
 */
export function filtersForTab(tab: InboxTabKey): (typeof NOTIFICATION_FILTERS)[number][] {
  const tabEvents = tab === 'cg' ? CG_EVENTS : tab === 'messages' ? MESSAGES_EVENTS : null;
  const usable = NOTIFICATION_FILTERS.filter((entry) => {
    if (tabEvents === null || entry.key === 'all' || entry.key === 'unread') {
      return true;
    }
    return FILTER_EVENTS[entry.key].some((event) => tabEvents.includes(event));
  });
  return [...usable];
}

/**
 * The events whose triggers write a cg_order_id, so filtersForTab can ask
 * rather than assume.
 *
 * A hand-written list like this is exactly the thing that goes stale — it
 * already did once while this drop was being written, because
 * sample_photos_uploaded gained an order in 20261121000100 and the first
 * version of this list did not know. So it is guarded: a test reads every
 * migration and fails if an event outside this list is ever inserted with a
 * cg_order_id, and if any of the three chips below could match one.
 *
 * Note this list does NOT decide which tab a row lands in. That is
 * isCgNotification, which reads the row's own order id and cannot go stale.
 * This list only decides which chips the CG tab is worth offering.
 */
export const CG_EVENTS: NotificationEvent[] = [
  'cg_status_changed',
  'po_sent',
  'client_signed_off',
  'cg_order_stalled',
  'po_revised',
  'samples_decided',
  'client_approved_design',
  'revision_answered',
  'supplier_requested_revision',
  'sample_photos_uploaded',
];

/**
 * The events a Messages notice can carry, so filtersForTab can ask rather
 * than assume which chips the Messages tab is worth offering. Same role as
 * CG_EVENTS, and like it this list does NOT decide which tab a row lands in
 * (isMessagesNotification reads the row's own marker for that).
 *
 * Where each comes from, read in the migrations on main 3eb95b5:
 * - mentioned: an @ in a room message or thread reply, and an @ in an Alex
 *   Huddle chat reply (comment_mentions_notify, 20270184000100:719-738).
 * - thread_reply: rooms only (task_comments_guard, 20270184000100:190-199).
 * - added_to_project: being added to a channel such as Agents
 *   (project_members_notify, 20270180000100:465-491 skips only DMs and groups).
 * - huddle_comment: Alex's own comment in the Huddle chat, if the database
 *   marks it (add_huddle_comment, 20270170000100:307). Listed so the chips
 *   stay honest either way; it matches no chip.
 * A test reads these back and fails if one leaves the enum.
 */
export const MESSAGES_EVENTS: NotificationEvent[] = [
  'mentioned',
  'thread_reply',
  'added_to_project',
  'huddle_comment',
];

/** The pipeline badge on a CG row, or '' when the order is unreadable. */
export function cgPipelineLabel(item: InboxItem): string {
  if (item.cg_order_type === null) {
    return '';
  }
  return item.cg_order_type === 'in_stock' ? 'In-Stock' : 'Custom';
}

const FILTER_EVENTS: Record<
  Exclude<NotificationFilterKey, 'all' | 'unread'>,
  NotificationEvent[]
> = {
  assigned: ['task_assigned', 'collaborator_added', 'added_to_project'],
  mentions: ['mentioned'],
  due: ['due_date_changed', 'task_due_tomorrow', 'task_overdue'],
};

/** The events one chip matches on, so a test can ask without re-listing them. */
export function filterEventsFor(key: NotificationFilterKey): NotificationEvent[] {
  if (key === 'all' || key === 'unread') {
    return [];
  }
  return [...FILTER_EVENTS[key]];
}

export function filterInboxItems(items: InboxItem[], filter: NotificationFilterKey): InboxItem[] {
  if (filter === 'all') {
    return items;
  }
  if (filter === 'unread') {
    return items.filter((item) => !item.is_read);
  }
  const events = FILTER_EVENTS[filter];
  return items.filter((item) => events.includes(item.event_type));
}

export function unreadCount(items: InboxItem[]): number {
  return items.reduce((count, item) => (item.is_read ? count : count + 1), 0);
}
